import { callDecimal, DecimalResult } from "../utils.ts";
import { Kind } from "../common-types/manageAssets.ts";
import { getContextToken } from "routes/home/my-courses/coursesUtils.ts";

export type PrintStatus =
  | "Do Not Issue"
  | "Issued"
  | "Returned"
  | "Not Returned"
  | "Return Refused"
  | "Not Issued";

export interface AccessCode {
  code: string;
  href: string;
}

export interface ContentAccess {
  vbid: string;
}

export interface ProgramFeature {
  bullet: "checkmark";
  text: "string";
}

export interface PrintInfo {
  hours: string;
  location: string;
  pickupDate: string;
  returnDate: string;
  pendingReturns: CourseDetails[];
  totalPenalty: number;
}

export interface SidebarLinks {
  label: string;
  href: string;
}

export interface CoursesContext {
  accessToken: string;
  defaultTermId: string | number;
  brand: {
    assets: {
      labels: {
        library: string;
        oer: string;
        web: string;
      };
      notFound: string;
    };
    ea: {
      programName: string;
      programDescription: string;
      programFeatures: ProgramFeature[];
      akademos: {
        updateShippingLink: string;
        trackingAndShippingLink: string;
        coursesLink: string;
        personalizedValueSheet: string;
      };
      catalogLevelOptOut: boolean;
      optOutMessage: string | null;
    };
    iaProgramName: string;
    iaProgramDescription: string;
    iaProgramFeatures: ProgramFeature[];
    optOutProgramName: string;
    optOutProgramDescription: string;
    printPickup: {
      disabled: boolean;
    };
    lms: {
      label: string;
    };
    userSurvey: {
      showToast: boolean;
      url: string;
    };
    sidebarLinks: SidebarLinks[];
  };
  // Is instructor in ANY courses (could be a TA)
  isInstructor: boolean;
  // Other types exist, not sure what they are
  links: { type: "vst_analytics"; link: string }[];

  // These are still being returned by the API but not sure if we need them
  // email: string;
  // first_name: string;
  // last_name: string;

  // These are still being returned by the API but we should not need them
  // course_dash_brand: string;
  // guid: string;
  // context_token: string;
}

export interface TermData {
  terms: Term[];
  unmatchedCourses: Course[];
}

export interface Term {
  id: string | number;
  institutionProgram: InstitutionProgram;
  programDecision: ProgramDecision;
  active: boolean;
  displayName: string;
  eaPrice: string;
  currency: string;
  endOn: string;
  courses: Course[];
  printInfo?: PrintInfo;
  optOutDeadlineDate: string;
  hasCompareCart: boolean;
}

interface VerbaCatalog {
  active: boolean;
  currency: string;
  displayName: string;
  ea: boolean;
  eaPrice: string;
  endOn: string;
  id: number;
  optOut: boolean;

  // These are still being returned by the API but we should not need them
  // optOutDeadlineDate: string;
}

interface CourseList {
  courses: Course[];
  verbaCatalogs: VerbaCatalog[];

  // These are still being returned by the API but not sure if we need them
  // accessToken: string;
  // email: string;
  // firstName: string;
  // guid: string;
  // id: number;
  // lastName: string;
  // links: [];
  // pdtSections: [];
  // standAloneLaunch: boolean;
  // tenantId: number;
  // verbaUserId: string;

  // These are still being returned by the API but we should not need them
  // isInstructor: boolean; // remove
}

export interface Course {
  id: number | string;
  title: string;
  optOutDeadline: string; // we need this for course level opt outs, keep if we don't find a different way to handle this
  userRole: "learner" | "instructor";

  // These are still being returned by the API but not sure if we need them
  // verbaCatalogId?: number;
  // active: boolean; // we need this unless decimal only returns active courses
}

export interface CourseDetails extends Course {
  books?: Book[];
  optOut: boolean;
  displayNotes: string;
  verbaCatalogId: number;

  // These are still being returned by the API but not sure if we need them
  // label: string;
  // startDate: string;
  // endDate: string;
  // censusDate: string;
  // dropDeadlineDate: string;
  // courseOptOutDeadlineDate: string; // is this different than optOutDeadline on course in course list?
  // verbaSection: VerbaSection;
}

// interface VerbaSection {
//   // These are still being returned by the API but not sure if we need them
//   posId: string;
// }

export interface Attachment {
  filename: string;
  url: string;
}

export type ButtonLinkType =
  | "open_book"
  | "launch_book"
  | "launch"
  | "ePOS"
  | "buy"
  | "redeem_code"
  | "launch_bundle_courseware"
  | "launch_bundle_reveal_code"
  | "launch_access_code"
  | "launch_reveal_code"
  | "lms_link"
  | "external_link"
  | "update_shipping"
  | "view_shipping";

export interface ButtonLink {
  type: ButtonLinkType;
  link: string;
}

export interface PurchaseLink {
  type: OptDecision;
  text: string;
}

export interface Book {
  id: string | number;
  currency: string | null;
  studentPrice: number | null;
  displayNotes: string | null;
  attachments: Attachment[];
  vbid: string;
  title: string;
  author: string;
  buttonLinks: ButtonLink[];
  optOut: boolean; // keep in some capacity... this means they are opted_out at item level
  requiredCode: "required" | "recommended" | "optional" | null;
  materialFormat:
    | "courseware"
    | "etextbook"
    | "print"
    | "bundle"
    | "library"
    | "oer"
    | "web_asset"
    | null;
  kind: Kind;
  verbaLicense: VerbaLicense;
  businessModels:
    | "Inclusive Access"
    | "Student Choice"
    | "ePOS"
    | "Institution Pay"
    | "Equitable Access";
  purchaseLinks: PurchaseLink[];
  materialStatus:
    | "opted_in"
    | "opted_out"
    | "not_included"
    | "included"
    | "continuation"
    | null;
  printStatus?: PrintStatus;
  replacementCost?: number;

  // These are still being returned by the API but not sure if we need them
  //   contents: [];
  //   partOfPackage: boolean | null;
  //   sku: string;
  //   bookType: string;
  //   customReader: boolean | null;
  //   onlineRentalDuration: string | null;
  //   companyId: number;
  //   studentPay: boolean;
  //   studentFacingIsbn: string;
  //   billingIsbn: string;
  //   verbaCatalogId: number;
  //   originId: number;

  // These are still being returned by the API but we should not need them
  // cover: string; // remove (if we are only using vbid)
}

export interface VerbaLicense {
  id: number;
  hidePrice: boolean;
  salesTaxEnabled: boolean;
  salesTaxExempt: boolean;
  studentPriceWithTax: number;

  // These are still being returned by the API but not sure if we need them
  // continuation: boolean;
  // provisional: boolean;
  // multiSection: boolean;
  // hideParticipation: boolean;
  // lockParticipation: boolean;
  // originId: number;
}

export type ProgramDecision =
  | "equitable_access"
  | "inclusive_access"
  | "no_program";

export type InstitutionProgram =
  | "flexible_access"
  | "equitable_access"
  | "inclusive_access";

export interface UpdateProgramDecision {
  decision: ProgramDecision;
  verbaCatalogId: string | number;
}

export type OptDecision = "opt_in" | "opt_out";

export interface ContentDecisionRequest {
  decision: OptDecision;
  reasonId: string;
}

export interface ContentDecisionResponse {
  decision: OptDecision;
  previousProgram: ProgramDecision;
  updatedProgram: ProgramDecision;
  verbaLicenseId: string;
  reasonId: number;
}

export interface OptOutReason {
  id: string;
  reason: string;
}

export const addCourseParams: (termId?: string | number) => string = (
  termId
) => {
  let params = "?json_key_style=camel";
  const token = getContextToken();
  if (token) {
    params += `&context_token=${token}`;
  }
  const brand = localStorage.getItem("myCoursesBrandOverride") ?? "";
  if (brand) {
    params += `&my_courses_brand_override=${brand}`;
  }
  if (termId) {
    params += `&term_id=${termId}`;
  }
  return params;
};

export async function getCourses() {
  return callDecimal<CourseList>(
    `/api/v1/my_courses/courses${addCourseParams()}`
  );
}

export async function getCourseDetails(
  id: string | number,
  termId?: string | number
) {
  return callDecimal<CourseDetails>(
    `/api/v1/my_courses/courses/${id}${addCourseParams(termId)}`
  );
}

export async function updateProgramDecision(
  catalogId: string | number,
  programDecision: ProgramDecision
) {
  return callDecimal<UpdateProgramDecision>(
    `/api/v1/my_courses/terms/${catalogId}${addCourseParams()}`,
    {
      method: "PUT",
      body: JSON.stringify({
        term: {
          program_decision: programDecision,
        },
      }),
    }
  );
}

export async function updateContentDecision(
  courseId: string | number,
  verbaLicenseId: string | number,
  contentDecision: ContentDecisionRequest
): Promise<DecimalResult<ContentDecisionResponse>> {
  return callDecimal<ContentDecisionResponse>(
    `/api/v1/my_courses/content_decision/${verbaLicenseId}${addCourseParams()}&course_id=${courseId}`,
    {
      method: "PUT",
      body: JSON.stringify(contentDecision),
    }
  );
}

export async function getOptOutReasons(): Promise<
  DecimalResult<OptOutReason[]>
> {
  return callDecimal<OptOutReason[]>(
    `/api/v1/my_courses/content_decision/opt_out_reasons${addCourseParams()}`
  );
}

export async function getTerms() {
  return callDecimal<TermData>(`/api/v1/my_courses/terms${addCourseParams()}`);
}

export async function getCoursesContext(): Promise<
  DecimalResult<CoursesContext>
> {
  const contextToken = getContextToken();
  const tokenSegment = contextToken ? `/${contextToken}` : "";
  return callDecimal<CoursesContext>(
    `/api/v1/my_courses/contexts${tokenSegment}${addCourseParams()}`
  );
}

export async function getAccessCode(
  url: string
): Promise<DecimalResult<AccessCode>> {
  return callDecimal<AccessCode>(
    `/api/v1/my_courses/access_codes/redeem${addCourseParams()}`,
    {
      method: "POST",
      body: JSON.stringify({ url }),
    }
  );
}

export async function getContentAccess(
  path: string
): Promise<DecimalResult<ContentAccess>> {
  return callDecimal<ContentAccess>(path);
}
